import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';

import Input from '../../../../components/Global/Input';
import SelectSimple from '../../../../components/Global/SelectSimple';
import TextArea from '../../../../components/Global/TextArea';
import { UploadMultiple } from '../../../../components/Global/UploadMultiple';
import { UserSelect } from '../../../../components/Global/UserSelect';
import { useAuth } from '../../../../hooks/auth';
import { useUsers } from '../../../../hooks/useUsers';
import getValidationErrors from '../../../../utils/getValidationErrors';
import {
  ActionDraft,
  FACTORY_OPTIONS,
  OCCURRENCE_TYPE_OPTIONS,
  nonConformitySchema,
} from '../../../../utils/qualityControlConstants';
import { qualityControlService } from '../../../../services/qualityControlService';
import { ActionsRepeater } from '../ActionsRepeater';
import { FormLetterhead } from '../FormLetterhead';
import { OccurrenceTypeCheckboxes } from '../OccurrenceTypeCheckboxes';
import { PageShell } from '../PageShell';
import { useDemoMode } from '../useDemoMode';

interface FormData {
  title: string;
  opened_at: string;
  occurrence_types: string[];
  factory_unit: string;
  supplier_or_producer: string;
  description: string;
  evidences: File[];
}

const NewNonConformity: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { user } = useAuth();
  const history = useHistory();
  const demo = useDemoMode();
  const toast = useToast();
  const { users: allUsers } = useUsers();

  const [correctiveActions, setCorrectiveActions] = useState<ActionDraft[]>([]);
  const [preventiveActions, setPreventiveActions] = useState<ActionDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // "Quem abriu" vem da mesma lista de usuários do "Responsável", já
  // preenchido com o usuário logado. A API grava o nome do cadastro desse
  // usuário (opened_by_user_id), não um texto livre.
  const [openedByUserId, setOpenedByUserId] = useState(user.id);
  const [responsibleUserId, setResponsibleUserId] = useState('');
  const responsibleName =
    allUsers.find(u => u.id === responsibleUserId)?.name || '';

  const handleSubmit = useCallback(
    async (data: FormData) => {
      try {
        setSubmitting(true);
        formRef.current?.setErrors({});

        // O usuário logado pode não estar na lista (ex.: lista ainda
        // carregando); nesse caso usa os dados da sessão.
        const openedByUser =
          allUsers.find(u => u.id === openedByUserId) ||
          (openedByUserId && openedByUserId === user.id
            ? { id: user.id, name: user.name }
            : undefined);

        if (!openedByUser) {
          toast({
            title: 'Informe quem abriu a NC',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        const responsibleUser = allUsers.find(u => u.id === responsibleUserId);

        if (!responsibleUser) {
          toast({
            title: 'Informe o responsável pela NC',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        if (!responsibleUser.email) {
          toast({
            title: 'Responsável sem e-mail cadastrado',
            description:
              'Esse usuário não tem e-mail cadastrado no sistema, então não será possível notificá-lo automaticamente.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }

        await nonConformitySchema.validate(data, { abortEarly: false });

        const actionDueDates = [...correctiveActions, ...preventiveActions]
          .map(action => action.due_date)
          .filter(Boolean);
        const dueDate =
          actionDueDates.length > 0
            ? actionDueDates.reduce((latest, current) =>
                current > latest ? current : latest,
              )
            : data.opened_at;

        const created = await qualityControlService.create(
          {
            title: data.title.trim(),
            opened_by_user_id: openedByUser.id,
            responsible: responsibleUser.name,
            responsible_user_id: responsibleUser.id,
            responsible_email: responsibleUser.email || null,
            opened_at: data.opened_at,
            due_date: dueDate,
            occurrence_types: data.occurrence_types,
            factory_unit: data.factory_unit as 'bovine' | 'swine',
            supplier_or_producer: data.supplier_or_producer || null,
            description: data.description,
            corrective_actions: correctiveActions,
            preventive_actions: preventiveActions,
          },
          openedByUser.id,
          openedByUser.name,
          demo,
        );

        const evidenceFiles = data.evidences || [];
        const failedEvidences: string[] = [];
        await Promise.all(
          evidenceFiles.map(file =>
            qualityControlService
              .uploadEvidence(created.id, file, demo)
              .catch(err => {
                console.error('Erro ao enviar evidência:', err);
                failedEvidences.push(file.name);
              }),
          ),
        );

        // Só agora o e-mail ao responsável sai, já com as evidências.
        let notified = true;
        await qualityControlService
          .notifyResponsible(created.id, demo)
          .catch(err => {
            console.error('Erro ao notificar o responsável:', err);
            notified = false;
          });

        toast({
          title: 'Não conformidade registrada',
          description: `${created.code} criada com sucesso.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        // A NC já existe: a falha de um anexo não desfaz o cadastro, mas
        // precisa ser vista para a evidência ser reenviada no detalhe.
        if (failedEvidences.length > 0) {
          toast({
            title: 'Algumas evidências não foram enviadas',
            description: `Envie novamente no detalhe da NC: ${failedEvidences.join(
              ', ',
            )}`,
            status: 'warning',
            duration: 9000,
            isClosable: true,
          });
        }

        if (!notified) {
          toast({
            title: 'Não foi possível enviar o e-mail ao responsável',
            description: `Avise o responsável sobre a ${created.code}.`,
            status: 'warning',
            duration: 9000,
            isClosable: true,
          });
        }

        // Quem abriu é o usuário logado: o detalhe já abre a assinatura do
        // registro. Se for outra pessoa, ela recebe o pedido por e-mail.
        const query = new URLSearchParams();
        if (demo) query.set('demo', '1');
        if (openedByUser.id === user.id) query.set('assinar', '1');
        const search = query.toString();

        history.push(
          `/quality-control/${created.id}${search ? `?${search}` : ''}`,
        );
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        } else {
          toast({
            title: 'Não foi possível salvar a NC',
            description:
              err instanceof Error ? err.message : 'Tente novamente.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      allUsers,
      correctiveActions,
      demo,
      history,
      openedByUserId,
      preventiveActions,
      responsibleUserId,
      toast,
      user.id,
      user.name,
    ],
  );

  const handleCancel = useCallback(() => {
    history.push(`/quality-control${demo ? '?demo=1' : ''}`);
  }, [demo, history]);

  return (
    <PageShell>
      <FormLetterhead demo={demo} />

      <Form ref={formRef} onSubmit={handleSubmit}>
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
          gridGap="4"
          mb="4"
        >
          <Box>
            <Text fontSize="sm" mb={1}>
              Quem abriu
            </Text>
            <UserSelect
              users={allUsers}
              value={openedByUserId}
              onChange={setOpenedByUserId}
              placeholder="Selecione quem abriu"
            />
          </Box>
          <Box>
            <Text fontSize="sm" mb={1}>
              Responsável pela NC
            </Text>
            <UserSelect
              users={allUsers}
              value={responsibleUserId}
              onChange={setResponsibleUserId}
              placeholder="Selecione o responsável"
            />
          </Box>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
          gridGap="4"
          mb="4"
        >
          <Box>
            <Text fontSize="sm" mb={1}>
              Abertura da NC
            </Text>
            <Input name="opened_at" type="date" />
          </Box>
          <Box>
            <Text fontSize="sm" mb={1}>
              Fábrica
            </Text>
            <SelectSimple
              name="factory_unit"
              options={FACTORY_OPTIONS}
              placeholder="Selecione a fábrica"
            />
          </Box>
        </Box>

        <Box mb="4">
          <Text fontSize="sm" mb={2}>
            Tipo de Ocorrência
          </Text>
          <OccurrenceTypeCheckboxes
            name="occurrence_types"
            options={OCCURRENCE_TYPE_OPTIONS}
          />
        </Box>

        <Box mb="4">
          <Text fontSize="sm" mb={1}>
            Título da Não Conformidade
          </Text>
          <Input
            name="title"
            type="text"
            placeholder="Resuma a não conformidade em poucas palavras"
          />
        </Box>

        <Box mb="4">
          <Text fontSize="sm" mb={1}>
            Fornecedor ou Produtor (se aplicável)
          </Text>
          <Input name="supplier_or_producer" type="text" />
        </Box>

        <Box mb="6">
          <Text fontSize="sm" mb={1}>
            Descrição e Causa da Não Conformidade
          </Text>
          <TextArea
            name="description"
            placeholder="Descreva a não conformidade e sua causa"
          />
        </Box>

        <Divider mb="6" />

        <Box mb="6">
          <Heading size="sm" mb={3}>
            Evidência da Não Conformidade
          </Heading>
          <UploadMultiple name="evidences" />
        </Box>

        <Divider mb="6" />

        <Box mb="6">
          <ActionsRepeater
            title="Plano de Ação - Ações Corretivas"
            addButtonLabel="Adicionar Ação Corretiva"
            value={correctiveActions}
            onChange={setCorrectiveActions}
            defaultResponsible={responsibleName}
          />
        </Box>

        <Box mb="6">
          <ActionsRepeater
            title="Plano de Ação - Ações Preventivas"
            addButtonLabel="Adicionar Ação Preventiva"
            value={preventiveActions}
            onChange={setPreventiveActions}
            defaultResponsible={responsibleName}
          />
        </Box>

        <Flex justify="flex-end" gridGap="3">
          <Button variant="outline" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button type="submit" colorScheme="blue" isLoading={submitting}>
            Salvar NC
          </Button>
        </Flex>
      </Form>
    </PageShell>
  );
};

export default NewNonConformity;
